import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import "./TypeMaterials.css";
import axiosInstance from "../../../Config/axiosInstance";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { DataGrid } from "@mui/x-data-grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";

import AddTypeMaterials from "./AddTypeMaterials";
import EditTypeMaterials from "./EditTypeMaterials";

import NotAuthorized from "../../../Components/ProtectedRoute/NotAuthorized";

const getPathFromUrl = (url) => {
  try {
    const { pathname } = new URL(url);
    return pathname;
  } catch (e) {
    console.error("Eroare la extragerea URL-ului:", e);
    return url;
  }
};

const TypeMaterialList = ({ canCreate, canEdit, canDelete }) => {
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const fetchTypeMaterial = async () => {
      try {
        const response = await axiosInstance.get("/type-materials/");
        //sorteaza descrescator dupa ID (cel mai recent primul)
        const sortedData = response.data.sort((a,b) => b.id - a.id);

        //campul nr pentru fiecare rand
        const dataWithNr = sortedData.map((item, index) => ({
          ...item,
          nr: sortedData.length - index
        }));

        setRows(dataWithNr);
      } catch (error) {
        console.error("Eroare la preluare tipurilor de materiale:", error);
      }
    };
    fetchTypeMaterial();
  }, []);

  const handleOpenDialog = (id) => {
    setSelectedTypeId(id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTypeId(null);
  };

  const handleConfirmDelete = async () => {
    try {
      await axiosInstance.delete(`/type-materials/${selectedTypeId}/`);
     setRows((prevRows) => {
        //elimina randul sters
        const filteredRows = prevRows.filter((row) => row.id !== selectedTypeId);

        //recalculeaza nr pentru toate randurile ramase
        const updatedRows = filteredRows.map((item, index) => ({
          ...item, 
          nr: filteredRows.length-index
        }));
        return updatedRows;
      });
    } catch (error) {
      const msg = error.response?.data?.detail || "An error occurred.";
      setErrorMessage(msg);
      setErrorDialogOpen(true);
    } finally {
      handleCloseDialog();
    }
  }

  const columns = [
    { field: "nr", headerName: "Nr.", minWidth: 100, flex: 1, headerClassName: "bold-header" },
    { field: "name", headerName: "Quality of material", minWidth: 300, flex: 2, headerClassName: "bold-header" },
    { field: "code", headerName: "Standard", minWidth: 300, flex: 2, headerClassName: "bold-header" },
    ...(canEdit || canDelete ? [
      {
        field: "actions",
        headerName: "Action",
        minWidth: 200,
        flex: 1,
        headerAlign: "center",
        renderCell: (params) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "2px",
              width: "100%",
              height: "100%",
            }}
          >
            {canEdit && (
              <IconButton
                size="small"
                color="primary"
                onClick={() => navigate(`/type-materials/edit/${params.row.id}`)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}

            {canDelete && (
              <IconButton
                size="small"
                color="error"
                onClick={() => handleOpenDialog(params.row.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </div>
        ),

      }
    ] : [])
  ];

  return (
    <>
      <div className="materials-page">
        <div className="header-container">
          <h2>TYPE OF MATERIALS</h2>
          <div className="materials-actions">
            {canCreate && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                sx={{ borderRadius: "10px" }}
                className="responsive-button"
                onClick={() => navigate("/type-materials/create")}
              >
                Add Type of Materials
              </Button>
            )}
          </div>
        </div>

        <Box className="materials-box-subcontainer materials-page materials-datagrid">
          <div className="materials-data-grid-container full-width-height custom-materials-grid">
            <DataGrid
              rows={rows}
              columns={columns}
              getRowHeight={() => "auto"}
              pageSize={5}
              initialState={{
                sorting: {
                  sortModel: [{ field: "id", sort: "desc" }],
                },
                pagination: {
                  paginationModel: { pageSize: 12 },
                },
              }}
              disableRowSelectionOnClick
              sx={{
                "& .MuiDataGrid-cell": {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: "1.4",
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "3px",
                  paddingBottom: "3px",
                },
                "& .MuiDataGrid-columnHeaders": {
                  whiteSpace: "normal",
                },
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
                "& .Mui-selected:hover": {
                  backgroundColor: "transparent !important",
                },
              }}
              pageSizeOptions={[10, 12, 20, 25]}
            />
          </div>
        </Box>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          aria-labelledby="confirm-delete-dialog"
        >
          <DialogTitle id="confirm-delete-dialog">Delete Confirmation</DialogTitle>
          <DialogContent>
            <Typography variant="h10">
              Are you sure you want to delete this type of materials? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>CLOSE</Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">CONFIRM DELETE</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={errorDialogOpen}
          onClose={() => setErrorDialogOpen(false)}
          aria-labelledby="error-dialog-title"
        >
          <DialogTitle id="error-dialog-title">Error</DialogTitle>
          <DialogContent>
            <Typography>{errorMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setErrorDialogOpen(false)} autoFocus>
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  )
}

const MaterialType = () => {
  const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
  const permissions = storedPermissions?.pages || [];

  const hasPermission = (path, checkPermission) => {
    const pagePermission = permissions.some((page) => getPathFromUrl(page.url) === path && checkPermission(page));
    return pagePermission;
  };

  const canCreate = hasPermission('/type-materials', (page) => page.can_create);
  const canEdit = hasPermission('/type-materials', (page) => page.can_edit);
  const canDelete = hasPermission('/type-materials', (page) => page.can_delete);

  return (
    <Routes>
      <Route path="/" element={<TypeMaterialList canCreate={canCreate} canEdit={canEdit} canDelete={canDelete} />} />
      <Route path="create" element={canCreate ? <AddTypeMaterials /> : <NotAuthorized />} />
      <Route path="edit/:id" element={canEdit ? <EditTypeMaterials /> : <NotAuthorized />} />
    </Routes>
  );
};

export default MaterialType;